import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/axios';

const MIN_PASSWORD_LENGTH = 8;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const isConfirmFlow = Boolean(token);
  const navigate = useNavigate();

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const title = useMemo(
    () => (isConfirmFlow ? 'Choose a new password' : 'Request password reset'),
    [isConfirmFlow]
  );

  const handleRequest = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setSuccessMessage('');

    try {
      await api.post('/auth/password-reset/request', { identifier });
      setSuccessMessage('If an account exists we have sent a reset link to the matching email.');
      setIdentifier('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Unable to start password reset.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async (event) => {
    event.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      toast.error(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    setSubmitting(true);
    setSuccessMessage('');

    try {
      await api.post('/auth/password-reset/confirm', { token, password });
      setSuccessMessage('Password updated. You can now return to the login screen.');
      setPassword('');
      setConfirmPassword('');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Unable to set new password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-12 text-white">
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-10 rounded-3xl border border-slate-800 bg-slate-900/70 p-8 lg:flex-row">
        <div className="flex-1 space-y-5">
          <p className="text-sm uppercase tracking-[0.35em] text-slate-500">Account security</p>
          <h1 className="text-4xl font-semibold text-white">{title}</h1>
          <p className="text-slate-400">
            {isConfirmFlow
              ? 'Pick a strong password to secure your register access. This link may expire after a short time.'
              : 'Enter the email address or username tied to your Ofoodi POS login. We will email you a link to set a new password.'}
          </p>
          <div className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4 text-sm text-slate-300">
            <p className="font-semibold text-white">Need help?</p>
            <p className="mt-2 text-slate-400">
              If you no longer have access to the registered email address, ask an administrator to invite you again
              from the Team screen.
            </p>
          </div>
        </div>

        <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-950/70 p-8">
          <h2 className="text-2xl font-semibold text-white">
            {isConfirmFlow ? 'Set password' : 'Send reset link'}
          </h2>
          <p className="mt-1 text-sm text-slate-400">
            {isConfirmFlow ? 'Choose a new password for your account.' : 'We’ll email a link if the account exists.'}
          </p>

          {successMessage && (
            <div className="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
              {successMessage}
            </div>
          )}

          <form
            onSubmit={isConfirmFlow ? handleConfirm : handleRequest}
            className="mt-6 space-y-4"
          >
            {!isConfirmFlow ? (
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-slate-500">
                  Email or username
                </label>
                <input
                  type="text"
                  className="mt-2 w-full rounded-2xl border border-slate-800 bg-slate-900 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-slate-500">New password</label>
                  <input
                    type="password"
                    className="mt-2 w-full rounded-2xl border border-slate-800 bg-slate-900 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-slate-500">Confirm password</label>
                  <input
                    type="password"
                    className="mt-2 w-full rounded-2xl border border-slate-800 bg-slate-900 px-4 py-3 text-base text-white focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                  />
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-2xl bg-emerald-500 py-3 text-center text-base font-semibold text-slate-900 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {submitting ? 'Processing...' : isConfirmFlow ? 'Save password' : 'Send reset link'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-400">
            <Link to="/login" className="font-semibold text-emerald-300 hover:text-emerald-200">
              Back to login
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
